import { useEffect, useMemo } from 'react'
import { DropzoneProps } from 'react-dropzone'
import { useFormContext } from 'react-hook-form'
import { useUploadDocument } from '@/api/accounting-dealer/file'
import { useDeleteDocument } from '@/api/accounting-dealer/file/delete-document'
import { queryClient } from '@/api/react-query'
import { Document, DocumentType, DocumentTypeEnum, EntityType } from '@/types'
import { DocumentDropzone } from './document-dropzone'

type FileTypes = 'pdf' | 'jpg' | 'jpeg' | 'png'

type DocumentInputProps = DropzoneProps & {
  entity: { key: string; type: EntityType }
  documents: Document[]
  documentType: DocumentType
  document_request_key?: string
  required?: boolean
  maxFiles?: number
  fieldName?: string
  label?: string
  acceptTypes?: FileTypes[]
}

export function DocumentInput({
  documentType,
  documents,
  entity,
  maxFiles = 5,
  required = false,
  fieldName,
  label,
  document_request_key,
  accept,
}: DocumentInputProps) {
  const { setValue } = useFormContext()

  const { mutate: uploadDocument, isPending: isUploading } = useUploadDocument({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['documents', entity.key, entity.type],
      })
    },
  })

  const { mutateAsync: deleteDocument } = useDeleteDocument({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['documents', entity.key, entity.type],
      })
    },
  })

  const filesFiltered = useMemo(
    () => documents?.filter((document) => document.type === documentType) ?? [],
    [documents, documentType],
  )

  useEffect(() => {
    if (!fieldName) return
    setValue(
      fieldName,
      filesFiltered.map((file) => file.key),
      { shouldValidate: true, shouldDirty: true },
    )
  }, [filesFiltered, fieldName, setValue])

  return (
    <DocumentDropzone
      label={label || DocumentTypeEnum[documentType]}
      required={required}
      maxFiles={maxFiles}
      uploading={isUploading}
      accept={accept}
      files={filesFiltered.map((file) => ({
        key: file.key,
        name: file.original_name,
        previewUrl: file.url,
        isPdf: file.mime_type === 'application/pdf',
      }))}
      onDrop={(accepted) => {
        accepted.forEach((file) => {
          const body = new FormData()
          body.append('file', file)
          uploadDocument({
            body,
            entity_key: entity.key,
            entity_type: entity.type,
            document_type: documentType,
            document_request_key,
          })
        })
      }}
      onRemove={(key) => deleteDocument({ document_key: key })}
    />
  )
}
